import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Divider } from '@mui/material';
import { BrandLoader, PageShell, toast } from '@core/components';
import { NotFound } from '@core/pages';
import { type Order, getOrderById } from '../../../api/order';
import OrderDetailHeader from './OrderDetailHeader';
import OrderDetailView from './OrderDetailView';
import {
  type OrderUpdatedHandler,
  useOrderDetailHandlers,
} from './useOrderDetailHandlers';

interface OrderDetailProps {
  orderId?: number;
  selectedItemId?: number | null;
  onDeleted?: () => void;
  onUpdated?: () => void;
}

const OrderDetail = ({
  orderId,
  selectedItemId,
  onDeleted,
  onUpdated,
}: OrderDetailProps) => {
  const { id: routeId } = useParams();
  const id = orderId ?? (routeId != null ? Number(routeId) : undefined);
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);

  const loadOrder = useCallback(async () => {
    if (id == null || Number.isNaN(id)) {
      setOrder(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setOrder(await getOrderById(id));
    } catch {
      toast.error('Failed to load order details');
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const { handleUpdated, historyRefreshKey } = useOrderDetailHandlers({
    onRefresh: loadOrder,
    onApplyOrder: setOrder,
  });

  const handleOrderUpdated = useCallback<OrderUpdatedHandler>(
    async (updated) => {
      await handleUpdated(updated);
      await onUpdated?.();
    },
    [handleUpdated, onUpdated]
  );

  const handleOrderDeleted = () => {
    if (onDeleted) {
      onDeleted();
      return;
    }
    navigate('/orders');
  };

  if (loading) {
    return (
      <PageShell>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            minHeight: 250,
          }}
        >
          <BrandLoader />
        </Box>
      </PageShell>
    );
  }

  if (!order) {
    return <NotFound />;
  }

  return (
    <PageShell
      header={
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <OrderDetailHeader
            order={order}
            onUpdated={handleOrderUpdated}
            onDeleted={handleOrderDeleted}
          />
          <Divider />
        </Box>
      }
    >
      <OrderDetailView
        order={order}
        selectedItemId={selectedItemId}
        onUpdated={handleOrderUpdated}
        historyRefreshKey={historyRefreshKey}
      />
    </PageShell>
  );
};

export default OrderDetail;
